import { useTheme } from "@mui/material";
import { ChartCard } from "@pages:app/analytics/components/cards/ChartCard";
import { DashboardGrid } from "@pages:app/analytics/components/cards/DashboardGrid";
import { DonutCard } from "@pages:app/analytics/components/cards/DonutCard";
import { StatCard } from "@pages:app/analytics/components/cards/StatCard";
import { AnalyticsData } from "@pages:app/analytics/tabs/useAnalyticsData";
import { useTranslation } from "react-i18next";
import {
  MdBarChart,
  MdOutlineGraphicEq,
  MdOutlineLanguage,
  MdOutlineMemory,
  MdOutlinePeople,
  MdOutlineSchedule,
} from "react-icons/md";

type AudioTabProps = {
  data: AnalyticsData;
};

export const AudioTab = ({ data }: AudioTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconColor = theme.palette.primary.main;
  const {
    summaryQuery,
    audioSummaryQuery,
    audioLanguageQuery,
    audioLanguageDurationQuery,
    activeUsersChart,
    requestsChart,
    transcribedChart,
    processingChart,
    audioLanguageSlices,
    audioLanguageDurationSlices,
    sumSlices,
    formatDuration,
    formatDurationCompact,
  } = data;

  const audio = audioSummaryQuery.data;
  const avgSeconds =
    audio && audio.totalTranscriptions > 0
      ? audio.totalSeconds / audio.totalTranscriptions
      : undefined;

  return (
    <DashboardGrid>
      {/* Row 1 */}
      <StatCard
        label={t("analytics.activeUsers")}
        value={summaryQuery.data?.activeUsersPeriodDistinct}
        icon={<MdOutlinePeople size={22} color={iconColor} />}
        colSpan={2}
      />
      <StatCard
        label={t("analytics.requests")}
        value={summaryQuery.data?.requestsPeriod}
        icon={<MdBarChart size={22} color={iconColor} />}
        colSpan={2}
      />
      <StatCard
        label={t("analytics.languages")}
        value={audioLanguageQuery.data?.length}
        icon={<MdOutlineLanguage size={22} color={iconColor} />}
        colSpan={2}
      />
      <StatCard
        label={t("analytics.transcribed")}
        value={audio?.totalSeconds}
        icon={<MdOutlineGraphicEq size={22} color={iconColor} />}
        colSpan={2}
        format={formatDuration}
      />
      <StatCard
        label={t("analytics.processingTime")}
        value={audio?.totalProcessingSeconds}
        icon={<MdOutlineMemory size={22} color={iconColor} />}
        colSpan={2}
        format={formatDuration}
      />
      <StatCard
        label={t("analytics.averageDuration")}
        value={avgSeconds}
        icon={<MdOutlineSchedule size={22} color={iconColor} />}
        colSpan={2}
        format={formatDuration}
      />

      {/* Row 2 */}
      <ChartCard
        title={t("analytics.activeUsers")}
        xAxis={activeUsersChart.xAxis}
        yAxis={activeUsersChart.yAxis}
        series={activeUsersChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />
      <ChartCard
        title={t("analytics.numberOfRequests")}
        xAxis={requestsChart.xAxis}
        yAxis={requestsChart.yAxis}
        series={requestsChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />

      {/* Row 3 */}
      <ChartCard
        title={t("analytics.transcribedAudio")}
        xAxis={transcribedChart.xAxis}
        yAxis={transcribedChart.yAxis}
        series={transcribedChart.series}
        colSpan={6}
        rowSpan={2}
        yValueFormatter={formatDurationCompact}
      />
      <ChartCard
        title={t("analytics.processingTime")}
        xAxis={processingChart.xAxis}
        yAxis={processingChart.yAxis}
        series={processingChart.series}
        colSpan={6}
        rowSpan={2}
        yValueFormatter={formatDurationCompact}
      />

      {/* Row 4 */}
      <DonutCard
        title={t("analytics.requestsByLanguage")}
        slices={audioLanguageSlices}
        centerValue={sumSlices(audioLanguageSlices)}
        centerLabel={t("analytics.unit.transcriptions")}
        loading={audioLanguageQuery.isLoading}
        emptyText={t("analytics.empty.transcriptions")}
        colSpan={6}
      />
      <DonutCard
        title={t("analytics.durationByLanguage")}
        slices={audioLanguageDurationSlices}
        centerValue={sumSlices(audioLanguageDurationSlices)}
        centerLabel={t("analytics.unit.transcribed")}
        loading={audioLanguageDurationQuery.isLoading}
        emptyText={t("analytics.empty.transcriptions")}
        colSpan={6}
        format={formatDuration}
      />
    </DashboardGrid>
  );
};
